import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Upload, Loader2, Video, FileText, ImageIcon, File, Users, Library, Play, ChevronLeft, Paperclip, X } from 'lucide-react';
import { getProductFileUploadUrl, requestMuxIngest, type CourseLesson, type MuxIngestStatus, type LessonAttachment } from '@/services/creatorApi';
import { createInterview, listInterviewClips, copyInterviewClip, type LibraryClip } from '@/services/interviewApi';
import LibraryPicker, { type StagedLibraryFile } from '@/components/library/LibraryPicker';
import VideoEditDrawer from '@/components/video-editor/VideoEditDrawer';
import RichText from './RichText';

interface LessonEditorProps {
  open: boolean;
  onClose: () => void;
  lesson: Partial<CourseLesson> | null;
  onSave: (lesson: Partial<CourseLesson>) => void;
  isSubscription: boolean;
  productId?: string;
}

// S3 single-PUT limit is 5GB; we're not using multipart so that's the hard ceiling.
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

function detectFileType(mime: string): 'video' | 'pdf' | 'image' | 'file' {
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  return 'file';
}

const LessonEditor: React.FC<LessonEditorProps> = ({ open, onClose, lesson, onSave, isSubscription, productId }) => {
  const [title, setTitle] = useState(lesson?.title || '');
  const [description, setDescription] = useState(lesson?.description || '');
  const [s3Key, setS3Key] = useState(lesson?.s3Key || '');
  const [thumbnailUrl, setThumbnailUrl] = useState(lesson?.thumbnailUrl || '');
  const [fileType, setFileType] = useState<'video' | 'pdf' | 'image' | 'file'>(lesson?.fileType || 'video');
  const [fileSize, setFileSize] = useState(lesson?.fileSize || 0);
  // Video library picker (interview clips recorded for this class)
  const [showLibrary, setShowLibrary] = useState(false);
  const [showMyLibrary, setShowMyLibrary] = useState(false);
  const [libraryClips, setLibraryClips] = useState<LibraryClip[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [importingKey, setImportingKey] = useState<string | null>(null);
  const [freePreview, setFreePreview] = useState(lesson?.freePreview ?? false);
  const [dripDay, setDripDay] = useState(lesson?.dripDay ?? 0);
  const [availableDate, setAvailableDate] = useState(lesson?.availableDate || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [startingInterview, setStartingInterview] = useState(false);
  // Streaming ingest fields — set at upload time (source-only ingest), carried
  // on the lesson, persisted by the next course save.
  const [muxAssetId, setMuxAssetId] = useState(lesson?.muxAssetId || '');
  const [muxPlaybackId, setMuxPlaybackId] = useState(lesson?.muxPlaybackId || '');
  const [muxIngestStatus, setMuxIngestStatus] = useState<MuxIngestStatus | ''>(lesson?.muxIngestStatus || '');
  // Inline video edit drawer (trim / audio / color / background on the current video).
  const [editingVideo, setEditingVideo] = useState(false);
  // Chapter attachments (PDFs / images) that ride along with this video lesson.
  const [attachments, setAttachments] = useState<LessonAttachment[]>(lesson?.attachments || []);
  const [attachUploading, setAttachUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);

  // Reset state when lesson changes
  React.useEffect(() => {
    setTitle(lesson?.title || '');
    setDescription(lesson?.description || '');
    setS3Key(lesson?.s3Key || '');
    setThumbnailUrl(lesson?.thumbnailUrl || '');
    setFileType(lesson?.fileType || 'video');
    setFileSize(lesson?.fileSize || 0);
    setFreePreview(lesson?.freePreview ?? false);
    setDripDay(lesson?.dripDay ?? 0);
    setAvailableDate(lesson?.availableDate || '');
    setMuxAssetId(lesson?.muxAssetId || '');
    setMuxPlaybackId(lesson?.muxPlaybackId || '');
    setMuxIngestStatus(lesson?.muxIngestStatus || '');
    setAttachments(lesson?.attachments || []);
  }, [lesson]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      alert('File too large. Maximum size is 5GB (S3 single-upload limit).');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(file.name, file.type);

      // Upload with progress tracking via XMLHttpRequest
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => xhr.status < 400 ? resolve() : reject(new Error('Upload failed'));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });

      setS3Key(key);
      setThumbnailUrl('');
      const detected = detectFileType(file.type);
      setFileType(detected);
      setFileSize(file.size);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));

      // New file replaces any prior video — drop stale streaming fields, then
      // start processing immediately (no save required). Failures are silent:
      // the save-time fan-out in CourseBuilderPage retries any video without
      // an asset, and the editor's auto-sync self-heals stuck rows.
      setMuxAssetId('');
      setMuxPlaybackId('');
      setMuxIngestStatus('');
      if (detected === 'video') {
        requestMuxIngest({ s3Key: key, kind: 'paid' })
          .then((r) => {
            if (!r) return; // feature off / unconfigured — save-time fan-out is also off
            setMuxAssetId(r.muxAssetId);
            setMuxPlaybackId(r.muxPlaybackId || '');
            setMuxIngestStatus(r.muxIngestStatus || 'preparing');
          })
          .catch((err) => console.warn('[video processing] upload-time ingest failed:', err));
      }
    } catch (err) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Upload a chapter file (PDF / image) and append it to this lesson's
  // attachments. Reuses the same product-file upload flow as the main content.
  const handleAttachUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (attachInputRef.current) attachInputRef.current.value = '';
    if (!file) return;
    if (attachments.length >= 10) { alert('You can attach up to 10 files per lesson.'); return; }
    if (file.size > MAX_FILE_SIZE) { alert('File too large. Maximum size is 5GB (S3 single-upload limit).'); return; }
    setAttachUploading(true);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(file.name, file.type);
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.onload = () => xhr.status < 400 ? resolve() : reject(new Error('Upload failed'));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });
      const fileType: 'pdf' | 'image' = file.type.startsWith('image/') ? 'image' : 'pdf';
      setAttachments(prev => [...prev, {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `att-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        title: file.name.replace(/\.[^.]+$/, ''),
        s3Key: key,
        fileType,
      }]);
    } catch (err) {
      alert('Attachment upload failed. Please try again.');
    } finally {
      setAttachUploading(false);
    }
  };

  const removeAttachment = (id: string) => setAttachments(prev => prev.filter(a => a.id !== id));

  const handleInterview = async () => {
    if (startingInterview) return;
    setStartingInterview(true);
    try {
      const { interview } = await createInterview({ title: title.trim() || 'Lesson interview', productId: productId || null });
      window.open(`/my-store/interviews/${interview._id}`, '_blank');
    } catch (err: any) {
      alert(err?.message || 'Could not start an interview room.');
    } finally { setStartingInterview(false); }
  };

  const openLibrary = async () => {
    if (!productId) return;
    setShowLibrary(true);
    setLibraryLoading(true);
    try { setLibraryClips(await listInterviewClips(productId)); }
    catch { setLibraryClips([]); }
    finally { setLibraryLoading(false); }
  };

  // Copy the chosen interview clip into a standard lesson video key, set it as
  // this lesson's video, and kick off streaming ingest (save-time fan-out also
  // covers it). The clip stays in the library — this is a copy, not a move.
  const pickClip = async (clip: LibraryClip) => {
    if (importingKey) return;
    setImportingKey(clip.s3Key);
    try {
      const { s3Key: newKey, thumbnailUrl: thumb } = await copyInterviewClip(clip.s3Key, clip.thumbnailUrl);
      setS3Key(newKey);
      setThumbnailUrl(thumb || '');
      setFileType('video');
      setFileSize(0);
      if (!title) setTitle(`Interview — ${clip.guestName}`);
      setMuxAssetId(''); setMuxPlaybackId(''); setMuxIngestStatus('');
      requestMuxIngest({ s3Key: newKey, kind: 'paid' })
        .then((r) => { if (r) { setMuxAssetId(r.muxAssetId); setMuxPlaybackId(r.muxPlaybackId || ''); setMuxIngestStatus(r.muxIngestStatus || 'preparing'); } })
        .catch((err) => console.warn('[library clip] ingest failed:', err));
      setShowLibrary(false);
    } catch (err: any) {
      alert(err?.message || 'Could not use that clip.');
    } finally { setImportingKey(null); }
  };

  // Full My Library pick (uploads / recordings / any interview take / enhancer
  // renders) — staged server-side into creator-products/ so viewer presign and
  // Mux ingest work, then wired in exactly like an upload.
  const pickFromMyLibrary = (f: StagedLibraryFile) => {
    setS3Key(f.key);
    setThumbnailUrl('');
    setFileType('video');
    setFileSize(0);
    if (!title) setTitle(f.name);
    setMuxAssetId(''); setMuxPlaybackId(''); setMuxIngestStatus('');
    requestMuxIngest({ s3Key: f.key, kind: 'paid' })
      .then((r) => { if (r) { setMuxAssetId(r.muxAssetId); setMuxPlaybackId(r.muxPlaybackId || ''); setMuxIngestStatus(r.muxIngestStatus || 'preparing'); } })
      .catch((err) => console.warn('[my-library pick] ingest failed:', err));
  };

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      ...lesson,
      title: title.trim(),
      description: description.trim() || undefined,
      s3Key,
      thumbnailUrl: thumbnailUrl || undefined,
      fileType,
      fileSize,
      freePreview,
      dripDay: availableDate ? 0 : dripDay,
      availableDate: availableDate || undefined,
      muxAssetId: muxAssetId || undefined,
      muxPlaybackId: muxPlaybackId || undefined,
      muxIngestStatus: muxIngestStatus || undefined,
      attachments,
    });
    onClose();
  };

  const fileIcon =
    fileType === 'pdf' ? <FileText className="w-5 h-5" style={{ color: 'hsl(0 70% 60%)' }} />
    : fileType === 'image' ? <ImageIcon className="w-5 h-5" style={{ color: 'var(--hj-deep-blue)' }} />
    : fileType === 'file' ? <File className="w-5 h-5" style={{ color: 'var(--hj-beige-ink-2)' }} />
    : <Video className="w-5 h-5" style={{ color: 'var(--hj-beige-ink-2)' }} />;

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--hj-beige-ink)',
    fontFamily: 'var(--hj-font-mono)',
    display: 'block',
    marginBottom: 6,
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl"
        data-paper="beige"
        style={{
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <DialogHeader>
          <DialogTitle asChild>
            <h2
              className="font-hj-display m-0"
              style={{ fontSize: 24, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}
            >
              {lesson?.id ? 'Edit lesson' : 'Add lesson'}
            </h2>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* File Upload */}
          <div>
            <Label style={labelStyle}>Content file</Label>
            {s3Key ? (
              <div
                className="flex flex-col gap-3"
                style={{
                  padding: 12,
                  borderRadius: 'var(--hj-r-sm)',
                  background: '#fff',
                  border: '1px solid var(--hj-beige-line)',
                }}
              >
                <div className="flex items-center gap-2">
                  {fileIcon}
                  <span
                    className="flex-1 truncate"
                    style={{ fontSize: 13, color: 'var(--hj-beige-ink)' }}
                  >
                    {s3Key.split('/').pop()}
                  </span>
                  {fileType === 'video' && (
                    <button
                      type="button"
                      onClick={() => setEditingVideo(true)}
                      className="inline-flex items-center gap-1.5"
                      style={{
                        padding: '5px 12px',
                        borderRadius: 999,
                        background: 'transparent',
                        border: '1px solid var(--hj-beige-line)',
                        color: 'var(--hj-beige-ink-2)',
                        fontSize: 12,
                        fontWeight: 500,
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <Video className="w-3.5 h-3.5" /> Edit video
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 999,
                      background: 'transparent',
                      border: '1px solid var(--hj-beige-line)',
                      color: 'var(--hj-beige-ink-2)',
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    Replace
                  </button>
                </div>
                {fileType === 'video' && (
                  <video
                    key={s3Key}
                    src={`/api/s3/${s3Key}`}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full"
                    aria-label={`Preview of uploaded video${title ? `: ${title}` : ''}`}
                    style={{
                      borderRadius: 'var(--hj-r-xs)',
                      background: '#000',
                      maxHeight: 360,
                    }}
                  />
                )}
                {fileType === 'image' && (
                  <img
                    src={`/api/s3/${s3Key}`}
                    alt={s3Key.split('/').pop()}
                    className="w-full"
                    style={{
                      borderRadius: 'var(--hj-r-xs)',
                      maxHeight: 360,
                      objectFit: 'contain',
                      background: 'var(--hj-beige-hover)',
                    }}
                  />
                )}
                {fileType === 'pdf' && (
                  <a
                    href={`/api/s3/${s3Key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 self-start"
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      background: 'transparent',
                      border: '1px solid var(--hj-beige-line)',
                      color: 'var(--hj-beige-ink-2)',
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: 'inherit',
                      textDecoration: 'none',
                    }}
                  >
                    Open PDF in new tab
                  </a>
                )}
              </div>
            ) : (
              <div
                className="text-center cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: 32,
                  borderRadius: 'var(--hj-r-sm)',
                  border: '1.5px dashed var(--hj-beige-line)',
                  background: '#fff',
                }}
              >
                {uploading ? (
                  <div className="flex flex-col gap-2 items-center">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--hj-beige-ink-3)' }} />
                    <Progress value={uploadProgress} className="h-1.5 w-full" />
                    <p className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>{uploadProgress}%</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-7 h-7 mx-auto" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 8 }} />
                    <p
                      className="m-0"
                      style={{ fontSize: 14, color: 'var(--hj-beige-ink-2)', fontWeight: 500 }}
                    >
                      Upload video, PDF, or file
                    </p>
                    <p className="m-0 mono-label" style={{ marginTop: 4, color: 'var(--hj-beige-ink-3)' }}>
                      Video, PDF, or file — up to 5GB
                    </p>
                  </>
                )}
              </div>
            )}
            {!s3Key && !uploading && (
              <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 10 }}>
                {productId && (
                  <button
                    type="button"
                    onClick={() => setShowMyLibrary(true)}
                    className="inline-flex items-center gap-1.5"
                    style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                  >
                    <Library className="w-3.5 h-3.5" /> Choose from library
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleInterview}
                  disabled={startingInterview}
                  className="inline-flex items-center gap-1.5"
                  style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit', cursor: startingInterview ? 'default' : 'pointer' }}
                >
                  {startingInterview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                  Record an interview with a guest
                </button>
              </div>
            )}
            <LibraryPicker open={showMyLibrary} stageTarget="lesson" onClose={() => setShowMyLibrary(false)} onPick={pickFromMyLibrary} />
      {showLibrary && (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 'var(--hj-r-sm)', background: '#fff', border: '1px solid var(--hj-beige-line)' }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
                  <button type="button" onClick={() => setShowLibrary(false)} className="inline-flex items-center gap-1" style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-2)', fontSize: 12.5 }}><ChevronLeft className="w-4 h-4" /> Back</button>
                  <span className="mono-label" style={{ marginLeft: 'auto', color: 'var(--hj-beige-ink-3)' }}>Video library</span>
                </div>
                {libraryLoading ? (
                  <div className="flex items-center justify-center" style={{ padding: 24 }}><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--hj-beige-ink-3)' }} /></div>
                ) : libraryClips.length === 0 ? (
                  <p className="m-0 text-center" style={{ padding: 16, fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}>No interview clips yet. Record an interview with a guest and finished takes show up here.</p>
                ) : (
                  <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                    {libraryClips.map(clip => (
                      <button key={clip.takeId} type="button" onClick={() => pickClip(clip)} disabled={!!importingKey}
                        className="relative" style={{ display: 'block', padding: 0, border: '1px solid var(--hj-beige-line)', borderRadius: 8, overflow: 'hidden', background: '#000', aspectRatio: '16/9', cursor: importingKey ? 'default' : 'pointer' }}>
                        {clip.thumbnailUrl ? <img src={clip.thumbnailUrl} alt={clip.guestName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div className="w-full h-full" style={{ background: '#111' }} />}
                        <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(0 0 0 / 0.2)' }}>
                          {importingKey === clip.s3Key ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#fff' }} /> : <Play className="w-5 h-5" style={{ color: '#fff', fill: '#fff', opacity: 0.9 }} />}
                        </span>
                        <span className="absolute" style={{ left: 5, bottom: 5, padding: '2px 6px', borderRadius: 6, background: 'oklch(0 0 0 / 0.6)', color: '#fff', fontSize: 10, fontWeight: 700 }}>{clip.guestName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
          </div>

          {/* Attachments — chapter files (PDFs / images) that ride with this lesson */}
          <div>
            <Label style={labelStyle}>Attachments</Label>
            {attachments.length > 0 && (
              <div className="flex flex-col gap-2" style={{ marginBottom: 10 }}>
                {attachments.map(a => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2"
                    style={{ padding: '9px 12px', borderRadius: 'var(--hj-r-sm)', background: '#fff', border: '1px solid var(--hj-beige-line)' }}
                  >
                    {a.fileType === 'image'
                      ? <ImageIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-deep-blue)' }} />
                      : <FileText className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(0 70% 60%)' }} />}
                    <span className="flex-1 truncate" style={{ fontSize: 13, color: 'var(--hj-beige-ink)' }}>
                      {a.title || a.s3Key.split('/').pop()}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      aria-label={`Remove ${a.title || 'attachment'}`}
                      style={{ background: 'transparent', border: 0, padding: 4, cursor: 'pointer', color: 'var(--hj-beige-ink-3)', display: 'inline-flex' }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => attachInputRef.current?.click()}
              disabled={attachUploading || attachments.length >= 10}
              className="inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
              style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', opacity: (attachUploading || attachments.length >= 10) ? 0.5 : 1 }}
            >
              {attachUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
              Attach a file
            </button>
            <p className="m-0 mono-label" style={{ marginTop: 6, color: 'var(--hj-beige-ink-3)' }}>
              PDFs or images that download alongside this lesson — up to 10.
            </p>
            <input ref={attachInputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleAttachUpload} />
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="lesson-title" style={labelStyle}>Title</Label>
            <Input
              id="lesson-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Lesson title"
            />
          </div>

          {/* Description (WYSIWYG) */}
          <div>
            <Label style={labelStyle}>Description (optional)</Label>
            <RichText
              value={description}
              onChange={setDescription}
              placeholder="What does this lesson cover?"
              minHeight={140}
            />
          </div>

          {/* Free Preview */}
          <div
            className="flex items-center justify-between"
            style={{
              padding: 14,
              borderRadius: 'var(--hj-r-sm)',
              background: '#fff',
              border: '1px solid var(--hj-beige-line)',
            }}
          >
            <div>
              <Label style={{ fontSize: 14, color: 'var(--hj-beige-ink)', fontWeight: 500 }}>Free preview</Label>
              <p style={{ marginTop: 2, fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}>
                Makes this <strong>entire lesson</strong> free to watch for anyone — including logged-out visitors — without buying. Use it as your teaser lesson.
              </p>
            </div>
            <Switch checked={freePreview} onCheckedChange={setFreePreview} />
          </div>

          {/* Release timing — same three choices as a series episode. Free
              lessons are always watchable, so the control hides for them. */}
          {!freePreview && (() => {
            const releaseMode: 'immediate' | 'day' | 'date' = availableDate ? 'date' : (dripDay > 0 ? 'day' : 'immediate');
            const anchor = isSubscription ? 'subscribing' : 'purchase';
            return (
              <div>
                <Label htmlFor="lesson-release" style={labelStyle}>Release</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    id="lesson-release"
                    value={releaseMode}
                    onChange={e => {
                      const mode = e.target.value;
                      if (mode === 'immediate') { setDripDay(0); setAvailableDate(''); }
                      else if (mode === 'day') { setAvailableDate(''); setDripDay(dripDay > 0 ? dripDay : 1); }
                      else { setDripDay(0); setAvailableDate(availableDate || new Date().toISOString().slice(0, 10)); }
                    }}
                    className="h-9"
                    style={{ borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 13, padding: '0 8px' }}
                  >
                    <option value="immediate">Available now</option>
                    <option value="day">Day N after {anchor}</option>
                    <option value="date">On a date</option>
                  </select>
                  {releaseMode === 'day' && (
                    <Input
                      id="lesson-drip"
                      type="number"
                      min={0}
                      value={dripDay}
                      onChange={e => setDripDay(parseInt(e.target.value) || 0)}
                      className="w-24"
                    />
                  )}
                  {releaseMode === 'date' && (
                    <Input
                      id="lesson-date"
                      type="date"
                      value={availableDate}
                      onChange={e => setAvailableDate(e.target.value)}
                      className="w-44"
                    />
                  )}
                  <span style={{ fontSize: 13, color: 'var(--hj-beige-ink-3)' }}>
                    {releaseMode === 'immediate' && 'Watchable as soon as they have access.'}
                    {releaseMode === 'day' && `Unlocks ${dripDay} day${dripDay !== 1 ? 's' : ''} after ${anchor}.`}
                    {releaseMode === 'date' && 'Unlocks for everyone on that date.'}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '9px 16px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-beige-line)',
              color: 'var(--hj-beige-ink-2)',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: 'pointer',
              minHeight: 38,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!title.trim()}
            className="disabled:cursor-not-allowed"
            style={{
              padding: '9px 18px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: !title.trim() ? 'not-allowed' : 'pointer',
              opacity: !title.trim() ? 0.5 : 1,
              minHeight: 38,
            }}
          >
            {lesson?.id ? 'Update' : 'Add'} lesson
          </button>
        </DialogFooter>
      </DialogContent>

      {/* Inline video edit drawer — applies the finalized clip to this editor's
          local state exactly like a Replace (new key, fresh Mux fields). */}
      {editingVideo && !!s3Key && fileType === 'video' && (
        <VideoEditDrawer
          open
          video={{ s3Key, title: title || undefined, muxPlaybackId: muxPlaybackId || undefined }}
          onApplied={(patch) => {
            setS3Key(patch.s3Key);
            setThumbnailUrl('');
            setFileType('video');
            setMuxAssetId(patch.muxAssetId || '');
            setMuxPlaybackId(patch.muxPlaybackId || '');
            setMuxIngestStatus(patch.muxIngestStatus || '');
          }}
          onClose={() => setEditingVideo(false)}
        />
      )}
    </Dialog>
  );
};

export default LessonEditor;
